<script setup lang="ts">
import ContactAddress from "@/components/ContactAddress.vue"
import LoadingButton from "@/components/LoadingButton.vue"
import VarsityContentProcessor from "@/components/VarsityContentProcessor.vue"
import { useMediaUrl } from "@/composables"
import { useShop, type ShopOrderSubmitPayload } from "@/composables/useShop"
import { OrderProcessState } from "@/model/order/order-process-state"
import type { PaymentMethod } from "@/model/payment/payment-method"
import MyDebitorProfile from "@/pages/App/My/MyDebitorProfile.vue"
import PaymentMethodSelect from "@/pages/Order/Payment/PaymentMethodSelect.vue"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, useLoadingState } from "@/vf"
import { computed, reactive, ref } from "vue"

const props = defineProps<{
    shopOrderId: string
    shopConfigurationId: string
    issueOwnerId?: string | null
    editable: boolean
    viewMode?: string
}>()

const emit = defineEmits<{
    (e: "submit", payload: ShopOrderSubmitPayload, done: () => void, paymentMethod?: PaymentMethod | null): void
}>()

const { uploadCdnUrl } = useMediaUrl()

const refreshProducts = ref<(() => Promise<void>) | undefined>()
defineExpose({ refreshProducts: () => refreshProducts.value?.() })

const shop = await useShop({
    shopOrderId: props.shopOrderId,
    shopConfigurationId: props.shopConfigurationId,
    issueOwnerId: props.issueOwnerId,
})

const cart = reactive(shop.cart)
const customer = reactive(shop.customer)
const { shopOrder, products, productItems, debitorProfiles, availableStock } = shop
refreshProducts.value = shop.refreshProducts

// Admins only update the order and never pay, so they are not asked for a payment method
const paymentMethods = computed(() => (props.viewMode === "admin" ? [] : cart.allowedPaymentMethods))

const isSubmitting = useLoadingState()

function handleSubmit() {
    isSubmitting.state = true
    emit(
        "submit",
        cart.buildPayload(),
        () => {
            isSubmitting.state = false
        },
        cart.selectedPaymentMethod,
    )
}

const isEditable = computed(() => {
    if (!props.editable) {
        return false
    }
    if (shopOrder.value.hasPayments) {
        return false
    }
    const state = shopOrder.value.orderProcess?.state
    return !(state === OrderProcessState.Closed || state === OrderProcessState.Canceled)
})
const mustHaveRegistration: boolean = !!shopOrder.value?.shopConfiguration?.championship

const trans = defineTranslations({
    "product": {
        de: "Produkt",
        en: "Product",
    },
    "name": {
        de: "Name",
        en: "Name",
    },
    "price": {
        de: "Preis",
        en: "Price",
    },
    "price_help": {
        de: "inkl. gesetzl. gültiger MwSt.",
        en: "incl. vat",
    },
    "quantity": {
        de: "Menge",
        en: "Quantity",
    },
    "total": {
        de: "Gesamt",
        en: "Total",
    },
    "debitor_profile": {
        de: "Rechnungsempfänger",
        en: "Billing Profile",
    },
    "action.order_now": {
        de: "Jetzt bestellen",
        en: "Order now",
    },
    "action.order_and_pay": {
        de: "Jetzt bestellen und bezahlen ({amount})",
        en: "Order and pay now ({amount})",
    },
    "action.update": {
        de: "Bestellung aktualisieren",
        en: "Update order",
    },
    "assignment.item_n": {
        de: "Exemplar {n}",
        en: "Item {n}",
    },
    "assignment.select_person": {
        de: "Person auswählen...",
        en: "Select person...",
    },
    "assignment.enter_name": {
        de: "Name eingeben...",
        en: "Enter name...",
    },
    "not_registered_for_championship": {
        de: "Deine Organisation ist nicht für die erforderliche Meisterschaft registriert. Dieses Produkt kann nicht bestellt werden.",
        en: "Your organization is not registered for the required championship. This product cannot be ordered.",
    },
    "no_members_found": {
        de: "Es wurden keine Mitglieder für die Meisterschaft gemeldet. Dieses Produkt kann nicht bestellt werden.",
        en: "No members have been registered for the championship. This product cannot be ordered.",
    },
    "framework_agreement_required": {
        de: "Für dieses Produkt ist eine Rahmenvereinbarung erforderlich. Du hast keine gültige Rahmenvereinbarung. Dieses Produkt kann nicht bestellt werden.",
        en: "This product requires a framework agreement. You do not have a valid framework agreement. This product cannot be ordered.",
    },
    "max_quantity_reached": {
        de: "Höchstmenge erreicht: {max}",
        en: "Maximum quantity reached: {max}",
    },
    "sold_out": {
        de: "Ausverkauft",
        en: "Sold out",
    },
    "stock_low": {
        de: "Nur noch {available} verfügbar",
        en: "Only {available} left",
    },
})

const submitLabel = computed(() => {
    if (props.viewMode === "admin") {
        return trans("action.update")
    }
    if (paymentMethods.value.length) {
        // The buyer picked a payment method, so ordering forwards straight to the checkout
        return trans("action.order_and_pay", { amount: $currency(cart.total, 100) })
    }

    return trans("action.order_now")
})
</script>

<template>
    <form @submit.prevent="handleSubmit()">
        <div class="py-5">
            <b>{{ trans("debitor_profile") }}</b>
            <template v-if="isEditable">
                <select
                    v-if="shopOrder.organization && debitorProfiles && debitorProfiles.length > 0"
                    v-model="cart.selectedDebitorProfile"
                    class="custom-select"
                    required
                >
                    <option value="">Please select...</option>
                    <option v-for="profile in debitorProfiles" :key="profile.id" :value="profile.id">
                        {{ profile.name }}
                    </option>
                </select>
                <div v-if="!shopOrder.organization">
                    <MyDebitorProfile
                        :editable="isEditable"
                        :account="viewMode === 'admin' ? issueOwnerId ?? undefined : undefined"
                        @loaded="profile => (cart.selectedDebitorProfile = profile.primaryDebitorProfile?.id)"
                    />
                </div>
            </template>
            <template v-else>
                <ContactAddress :address="shopOrder.debitorProfile" class="mb-3" />
            </template>
        </div>

        <div class="py-5">
            <table class="table table-striped">
                <thead>
                    <tr>
                        <th style="width: 10rem">{{ trans("product") }}</th>
                        <th style="width: 50%">{{ trans("name") }}</th>
                        <th></th>
                        <th class="text-right">{{ trans("price") }}</th>
                        <th style="width: 10rem">{{ trans("quantity") }}</th>
                        <th class="text-right">{{ trans("total") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="product in products" :key="product.id">
                        <template v-for="(item, itemIndex) in productItems(product)" :key="item.id">
                            <tr>
                                <td v-if="itemIndex === 0" :rowspan="productItems(product).length">
                                    <img
                                        v-if="product.images?.length"
                                        :src="uploadCdnUrl(product.images[0].filename!)"
                                        :alt="product.images[0].altText"
                                        style="width: 80px; height: 80px; object-fit: cover"
                                        class="rounded"
                                    />
                                </td>
                                <td v-if="itemIndex === 0" :rowspan="productItems(product).length">
                                    <div>{{ product.name }}</div>
                                    <varsity-content-processor :content="product.shortDescription" />
                                    <div v-if="(product.minOrderQuantity || 1) > 1">
                                        <small class="text-muted">
                                            Mindestbestellmenge: {{ product.minOrderQuantity }} (Aktuell:
                                            {{ cart.getProductTotalQuantity(product) }})
                                        </small>
                                    </div>
                                    <div
                                        v-if="
                                            cart.getMaxOrderQuantity(product) !== undefined &&
                                            cart.getProductTotalQuantity(product) >= cart.getMaxOrderQuantity(product)!
                                        "
                                    >
                                        <small class="text-danger">
                                            {{
                                                trans("max_quantity_reached", {
                                                    max: cart.getMaxOrderQuantity(product),
                                                })
                                            }}
                                        </small>
                                    </div>
                                    <div
                                        v-if="
                                            customer.notRegisteredForChampionship &&
                                            mustHaveRegistration &&
                                            product.isNominalRegistrationRequired
                                        "
                                        class="alert alert-warning mt-2 mb-0 py-1 px-2"
                                    >
                                        <small>{{ trans("not_registered_for_championship") }}</small>
                                    </div>
                                    <div
                                        v-else-if="customer.noMembersFound && product.isNominalRegistrationRequired"
                                        class="alert alert-warning mt-2 mb-0 py-1 px-2"
                                    >
                                        <small>{{ trans("no_members_found") }}</small>
                                    </div>
                                    <div
                                        v-if="
                                            !shopOrder.organization &&
                                            product.isFrameworkAgreementRequired &&
                                            !customer.hasActiveFrameworkAgreement
                                        "
                                        class="alert alert-warning mt-2 mb-0 py-1 px-2"
                                    >
                                        <small>{{ trans("framework_agreement_required") }}</small>
                                    </div>
                                    <template v-if="product.isUserAssignmentRequired && !shopOrder.organization">
                                        <div v-if="cart.getProductTotalQuantity(product) > 0" class="mt-1">
                                            <small class="text-muted">
                                                {{ shopOrder.debitorProfile?.name }}
                                            </small>
                                        </div>
                                    </template>
                                    <template v-else-if="product.isUserAssignmentRequired">
                                        <div v-for="variantItem in productItems(product)" :key="variantItem.id">
                                            <template v-if="cart.getItemQuantity(variantItem.id!) > 0">
                                                <div
                                                    v-for="position in cart.getItemQuantity(variantItem.id!)"
                                                    :key="position"
                                                    class="d-flex align-items-center gap-2 mt-1"
                                                >
                                                    <small class="text-muted text-nowrap mr-2">
                                                        <template v-if="product.variants?.length">
                                                            {{ (variantItem as any).name }} –
                                                        </template>
                                                        {{ trans("assignment.item_n", { n: position }) }}
                                                    </small>
                                                    <template v-if="isEditable">
                                                        <select
                                                            v-if="product.isNominalRegistrationRequired"
                                                            class="form-control form-control-sm"
                                                            :value="cart.getAssignment(variantItem.id!, position)?.account?.id ?? ''"
                                                            @change="
                                                                cart.setAssignment(
                                                                    variantItem.id!,
                                                                    position,
                                                                    customer
                                                                        .getAssignableMembers(product)
                                                                        .find(
                                                                            m =>
                                                                                m.id ===
                                                                                ($event.target as HTMLSelectElement)
                                                                                    .value,
                                                                        )!,
                                                                )
                                                            "
                                                            required
                                                        >
                                                            <option value="" disabled>
                                                                {{ trans("assignment.select_person") }}
                                                            </option>
                                                            <option
                                                                v-for="member in customer.getAssignableMembers(product)"
                                                                :key="member.id"
                                                                :value="member.id"
                                                            >
                                                                {{ member.name }}
                                                            </option>
                                                        </select>
                                                        <input
                                                            v-else
                                                            type="text"
                                                            class="form-control form-control-sm"
                                                            :placeholder="trans('assignment.enter_name')"
                                                            :value="cart.getAssignment(variantItem.id!, position)?.assignedName ?? ''"
                                                            @input="
                                                                cart.setAssignment(
                                                                    variantItem.id!,
                                                                    position,
                                                                    ($event.target as HTMLInputElement).value,
                                                                )
                                                            "
                                                            required
                                                        />
                                                    </template>
                                                    <template v-else>
                                                        <span>
                                                            {{
                                                                cart.getAssignment(variantItem.id!, position)?.account
                                                                    ?.name ??
                                                                cart.getAssignment(variantItem.id!, position)
                                                                    ?.assignedName
                                                            }}
                                                        </span>
                                                    </template>
                                                </div>
                                            </template>
                                        </div>
                                    </template>
                                </td>
                                <td>
                                    <div v-if="product.variants?.length">{{ (item as any).name }}</div>
                                </td>
                                <td class="text-right">
                                    {{ $currency(cart.getBestPrice(item, cart.getItemQuantity(item.id!) || 1)!, 100) }}
                                    <small class="d-block text-muted">{{ trans("price_help") }}</small>
                                </td>
                                <td>
                                    <template v-if="isEditable && availableStock(item) === 0">
                                        <span class="badge badge-danger">{{ trans("sold_out") }}</span>
                                    </template>
                                    <template v-else>
                                        <input
                                            type="number"
                                            class="form-control"
                                            min="0"
                                            :value="cart.getItemQuantity(item.id!)"
                                            :max="cart.getMaxOrderQuantity(product, item)"
                                            @input="
                                                cart.updateQuantity(
                                                    product,
                                                    item,
                                                    parseInt(($event.target as HTMLInputElement).value) || 0,
                                                )
                                            "
                                            :disabled="
                                                !isEditable ||
                                                !item.isPurchasable ||
                                                cart.getBestPrice(item, 1) === null ||
                                                ((customer.notRegisteredForChampionship || customer.noMembersFound) &&
                                                    product.isNominalRegistrationRequired &&
                                                    mustHaveRegistration) ||
                                                (!shopOrder.organization &&
                                                    product.isFrameworkAgreementRequired &&
                                                    !customer.hasActiveFrameworkAgreement)
                                            "
                                        />
                                        <small
                                            v-if="isEditable && availableStock(item) !== null && availableStock(item)! <= 5 && availableStock(item)! > 0"
                                            class="text-warning d-block"
                                        >
                                            {{ trans("stock_low", { available: availableStock(item) }) }}
                                        </small>
                                    </template>
                                </td>
                                <td class="text-right">
                                    {{
                                        cart.getBestPrice(item, cart.getItemQuantity(item.id!)) !== null
                                            ? $currency(
                                                  cart.getBestPrice(item, cart.getItemQuantity(item.id!))! *
                                                      cart.getItemQuantity(item.id!),
                                                  100,
                                              )
                                            : "–"
                                    }}
                                </td>
                            </tr>
                        </template>
                    </template>
                </tbody>
                <tfoot>
                    <tr>
                        <th colspan="5" class="text-right">{{ trans("total") }}:</th>
                        <th class="text-right">{{ $currency(cart.total, 100) }}</th>
                    </tr>
                </tfoot>
            </table>
        </div>

        <PaymentMethodSelect
            v-if="isEditable && paymentMethods.length"
            v-model="cart.selectedPaymentMethod"
            :methods="paymentMethods"
        />

        <div class="text-right py-5" v-if="isEditable">
            <LoadingButton
                type="submit"
                class="btn btn-primary ml-auto"
                :loading="isSubmitting"
                :disabled="!cart.canOrder || (paymentMethods.length > 0 && !cart.selectedPaymentMethod)"
            >
                {{ submitLabel }}
            </LoadingButton>
        </div>
    </form>
</template>
